import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Search, ChevronDown, Users, Check } from 'lucide-react';

interface UserOption {
  user_id: string;
  user_first_name: string;
  user_email: string;
  user_company_name: string;
  total_campaigns: number;
}

interface UserFilterDropdownProps {
  selectedUserId: string | null;
  onSelectUser: (userId: string | null) => void;
  className?: string;
}

export function UserFilterDropdown({ selectedUserId, onSelectUser, className = '' }: UserFilterDropdownProps) {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserOption[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_user_campaign_counts');

      if (error) {
        console.error('Error loading users:', error);
        return;
      }

      setUsers(data || []);
    } catch (err) {
      console.error('Exception loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter(user =>
      user.user_first_name.toLowerCase().includes(query) ||
      user.user_email.toLowerCase().includes(query) ||
      user.user_company_name.toLowerCase().includes(query)
    );

    setFilteredUsers(filtered);
  };

  const handleSelectUser = (userId: string | null) => {
    onSelectUser(userId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const getSelectedUserLabel = () => {
    if (!selectedUserId) {
      return 'Alle gebruikers';
    }

    const selectedUser = users.find(u => u.user_id === selectedUserId);
    if (!selectedUser) {
      return 'Gebruiker niet gevonden';
    }

    return `${selectedUser.user_first_name} (${selectedUser.user_company_name || selectedUser.user_email})`;
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="input w-full flex items-center justify-between gap-2 cursor-pointer hover:border-gray-400 transition-colors"
        type="button"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="truncate">{getSelectedUserLabel()}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-hidden flex flex-col">
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Zoek gebruiker..."
                className="input pl-10 w-full"
                autoFocus
              />
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                Gebruikers laden...
              </div>
            ) : (
              <>
                <button
                  onClick={() => handleSelectUser(null)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                    selectedUserId === null ? 'bg-blue-50' : ''
                  }`}
                  type="button"
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900">Alle gebruikers</div>
                      <div className="text-xs text-gray-500">
                        {users.length} gebruiker{users.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  {selectedUserId === null && (
                    <Check className="w-4 h-4 text-blue-600" />
                  )}
                </button>

                {filteredUsers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    Geen gebruikers gevonden
                  </div>
                ) : (
                  filteredUsers.map(user => (
                    <button
                      key={user.user_id}
                      onClick={() => handleSelectUser(user.user_id)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                        selectedUserId === user.user_id ? 'bg-blue-50' : ''
                      }`}
                      type="button"
                    >
                      <div>
                        <div className="font-medium text-gray-900">
                          {user.user_first_name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {user.user_company_name && `${user.user_company_name} • `}
                          {user.user_email}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {user.total_campaigns} campaign{user.total_campaigns !== 1 ? 's' : ''}
                        </div>
                      </div>
                      {selectedUserId === user.user_id && (
                        <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
